import React, { useState, useEffect, useRef } from 'react';
import { Play, Square, RefreshCw, AlertCircle, Database, Users, FileText, Activity, Clock } from 'lucide-react';
import batchManagementService from '../../../services/batchManagementService';
import { supabase } from '../../../lib/supabase'
; // or wherever your supabase client is configured
import { toArray } from '../../../utils/normalize';

export default function BatchProgressMonitor() {
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batches, setBatches] = useState([]);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(null);
  const [progressHistory, setProgressHistory] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const monitoringRef = useRef(null);

  useEffect(() => {
    loadBatches();
  }, []);

  useEffect(() => {
    // Stop monitoring when component unmounts
    return () => {
      if (monitoringRef?.current && typeof monitoringRef?.current === 'function') {
        monitoringRef?.current();
      }
    };
  }, []);

  const loadBatches = async () => {
    setIsLoading(true);
    try {
      // Get recent batches that might be processing
      const { data, error } = await supabase
        ?.from('upload_batches')
        ?.select(`
          id,
          filename,
          status,
          upload_date,
          rows_in_file,
          rows_inserted,
          processed_records,
          duplicate_records,
          rows_errors
        `)
        ?.in('status', ['processing', 'queued', 'completed'])
        ?.order('upload_date', { ascending: false })
        ?.limit(20);

      if (error) {
        throw error;
      }

      // Use safe array handling to prevent .find() errors
      setBatches(toArray(data));
    } catch (err) {
      setError('Failed to load batches: ' + err?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const startMonitoring = (batch) => {
    setSelectedBatch(batch);
    setCurrentProgress(null);
    setProgressHistory([]);
    setError('');
    setIsMonitoring(true);

    // Stop any existing monitoring
    if (monitoringRef?.current && typeof monitoringRef?.current === 'function') {
      monitoringRef?.current();
    }

    // Start new monitoring with error-resistant data handling
    monitoringRef.current = batchManagementService?.monitorBatchProgress(
      batch?.id,
      (progressData) => {
        // Safely handle progress data
        const safeProgressData = progressData || {};
        setCurrentProgress(safeProgressData);
        setProgressHistory(prev => {
          const prevArray = toArray(prev);
          return [
            ...prevArray?.slice(-20), // Keep last 20 entries
            {
              timestamp: new Date(),
              ...safeProgressData
            }
          ];
        });
      }
    );
  };

  const stopMonitoring = () => {
    if (monitoringRef?.current && typeof monitoringRef?.current === 'function') {
      monitoringRef?.current();
      monitoringRef.current = null;
    }
    setIsMonitoring(false);
  };

  const getProgressPercentage = (progress) => {
    if (!progress || progress?.rawRows === 0) return 0;
    return Math.round((progress?.finalPersons / progress?.rawRows) * 100);
  };

  const getHealthStatus = (progress) => {
    if (!progress) return 'unknown';
    
    if (progress?.progressionHealth === 'Healthy progression') return 'healthy';
    if (progress?.progressionHealth === 'Stuck in raw stage') return 'stuck-raw';
    if (progress?.progressionHealth === 'Stuck in persons stage') return 'stuck-persons';
    return 'unknown';
  };

  const getHealthColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600';
      case 'stuck-raw': return 'text-red-600';
      case 'stuck-persons': return 'text-orange-600';
      default: return 'text-gray-600';
    }
  };

  const formatTimestamp = (timestamp) => {
    return timestamp?.toLocaleTimeString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Live Batch Progress Monitor
          </h2>
          <div className="flex gap-2">
            <button
              onClick={loadBatches}
              disabled={isLoading}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4 inline mr-1" />
              Refresh Batches
            </button>
          </div>
        </div>

        {/* Batch Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Batch to Monitor
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {batches?.map(batch => (
              <div
                key={batch?.id}
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedBatch?.id === batch?.id
                    ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => startMonitoring(batch)}
              >
                <div className="font-medium text-sm text-gray-900 truncate">
                  {batch?.filename}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    batch?.status === 'completed' ? 'bg-green-100 text-green-800' :
                    batch?.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                    batch?.status === 'queued' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {batch?.status}
                  </span>
                  <div className="text-xs text-gray-500">
                    {batch?.rows_inserted || 0} / {batch?.rows_in_file || 0}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        </div>
      )}
      {/* Monitoring Controls */}
      {selectedBatch && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Monitoring: {selectedBatch?.filename}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                {isMonitoring ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <Activity className="w-4 h-4" />
                    <span className="text-sm">Live monitoring active</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Monitoring stopped</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {!isMonitoring ? (
                <button
                  onClick={() => startMonitoring(selectedBatch)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Play className="w-4 h-4 inline mr-1" />
                  Start Monitoring
                </button>
              ) : (
                <button
                  onClick={stopMonitoring}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <Square className="w-4 h-4 inline mr-1" />
                  Stop Monitoring
                </button>
              )}
            </div>
          </div>

          {/* Current Progress Display */}
          {currentProgress && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-600">Raw Rows</span>
                </div>
                <div className="text-2xl font-bold text-blue-900">
                  {currentProgress?.rawRows}
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-600">Final Persons</span>
                </div>
                <div className="text-2xl font-bold text-green-900">
                  {currentProgress?.finalPersons}
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-purple-600" />
                  <span className="text-sm text-purple-600">Final Arrests</span>
                </div>
                <div className="text-2xl font-bold text-purple-900">
                  {currentProgress?.finalArrests}
                </div>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-orange-600" />
                  <span className="text-sm text-orange-600">Failed Rows</span>
                </div>
                <div className="text-2xl font-bold text-orange-900">
                  {currentProgress?.deadletterRows}
                </div>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          {currentProgress && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Processing Progress
                </span>
                <span className="text-sm text-gray-500">
                  {getProgressPercentage(currentProgress)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-1000"
                  style={{ width: `${getProgressPercentage(currentProgress)}%` }}
                ></div>
              </div>
              <div className={`text-sm mt-2 ${getHealthColor(getHealthStatus(currentProgress))}`}>
                Status: {currentProgress?.progressionHealth}
              </div>
            </div>
          )}

          {/* Progress History */}
          {progressHistory?.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Progress History</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Time
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Raw Rows
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Persons
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Arrests
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Failed
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Health
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {toArray(progressHistory)?.slice(-10)?.reverse()?.map((entry, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {formatTimestamp(entry?.timestamp)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {entry?.rawRows || 0}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {entry?.finalPersons || 0}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {entry?.finalArrests || 0}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {entry?.deadletterRows || 0}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm ${getHealthColor(getHealthStatus(entry))}`}>
                          {entry?.progressionHealth || 'Unknown'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">How to Use Live Monitor</h3>
        <div className="text-sm text-blue-700 space-y-1">
          <p>1. Select a batch from the list above (processing or queued batches recommended)</p>
          <p>2. Click "Start Monitoring" to begin live progress tracking</p>
          <p>3. Watch real-time progression from raw data → persons → arrests</p>
          <p>4. Monitor for stuck stages (no progress for several minutes)</p>
          <p>5. Use the progress history to identify processing bottlenecks</p>
        </div>
      </div>
    </div>
  );
}